/**
 * Ficha de un producto de merch. Las variantes guardan precio, stock y Stripe.
 */

import { useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { useLocation, useParams } from 'react-router-dom';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { ResourceDetail } from '@/components/shared/ResourceDetail';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatEurFromCents, formatEurInput } from '@/lib/format-es';
import { catalogImageUrl } from '../fichas/lib/catalogImageUrl';
import { notify } from '@/lib/notify';
import { storeProductsListHrefFrom } from '@/lib/store-products-return';
import {
    useStoreMerchCatalog,
    useUpdateStoreMerchProduct,
    useUpdateStoreMerchVariant,
} from '../propios/hooks/useStorePropios';
import type { StoreMerchProduct, StoreMerchVariant } from '../propios/types';
import {
    merchBaselineAfterProductSave,
    merchBaselineAfterVariantSave,
    merchDraftIsDirty,
    merchDraftSnapshot,
    type MerchDraft,
    type MerchVariantDraft,
} from './productDetailModel';
import { MerchStockBoard } from './MerchStockPage';
import { useWarnUnsavedLeave } from './useWarnUnsavedLeave';

const CATEGORIES = [
    { value: 'ropa', label: 'Ropa' },
    { value: 'accesorios', label: 'Accesorios' },
] as const;

function parseEurToCents(raw: string): number | null {
    const text = raw.trim().replace(',', '.');
    if (!text) return null;
    const value = Number.parseFloat(text);
    if (!Number.isFinite(value) || value <= 0) return null;
    return Math.round(value * 100);
}

function draftFrom(product: StoreMerchProduct): MerchDraft {
    return {
        name: product.name,
        description: product.description ?? '',
        category: product.category,
        sortOrder: String(product.sortOrder ?? 0),
        variants: product.variants.map((variant) => ({
            id: variant.id,
            priceEur: formatEurInput(variant.priceCents / 100),
            stock: variant.stock == null ? '' : String(variant.stock),
            stripePriceId: variant.stripePriceId ?? '',
            isActive: variant.isActive,
        })),
    };
}

export function StoreMerchDetailPage() {
    const { merchId } = useParams<{ merchId: string }>();
    const location = useLocation();
    const productsHref = storeProductsListHrefFrom(location.state) ?? '/store/products';
    const catalog = useStoreMerchCatalog();
    const updateProduct = useUpdateStoreMerchProduct();
    const updateVariant = useUpdateStoreMerchVariant();
    const product = (catalog.data ?? []).find((item) => item.id === merchId) ?? null;
    const [draft, setDraft] = useState<MerchDraft | null>(null);
    const [baseline, setBaseline] = useState('');
    const [hydratedId, setHydratedId] = useState<string | null>(null);
    const [confirmActive, setConfirmActive] = useState(false);

    if (product && product.id !== hydratedId) {
        const next = draftFrom(product);
        setHydratedId(product.id);
        setDraft(next);
        setBaseline(merchDraftSnapshot(next));
    }

    const dirty = draft != null && baseline !== '' && merchDraftIsDirty(baseline, draft);
    const leave = useWarnUnsavedLeave(dirty);

    const setVariant = (id: string, patch: Partial<MerchVariantDraft>) => {
        setDraft((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                variants: prev.variants.map((variant) => (
                    variant.id === id ? { ...variant, ...patch } : variant
                )),
            };
        });
    };

    const onSave = async () => {
        if (!product || !draft) return;
        if (!draft.name.trim()) {
            notify({ type: 'err', text: 'El nombre no puede quedar vacío' });
            return;
        }
        try {
            await updateProduct.mutateAsync({
                productId: product.id,
                name: draft.name.trim(),
                description: draft.description.trim() || null,
                category: draft.category,
                sortOrder: Number.parseInt(draft.sortOrder, 10) || 0,
                isActive: product.isActive,
            });
            setBaseline(merchBaselineAfterProductSave(baseline, draft));
            notify({ type: 'ok', text: 'Producto guardado' });
        } catch (error: unknown) {
            notify({ type: 'err', text: error instanceof Error ? error.message : String(error) });
        }
    };

    const onToggleActive = async () => {
        if (!product) return;
        try {
            await updateProduct.mutateAsync({
                productId: product.id,
                isActive: !product.isActive,
            });
            notify({ type: 'ok', text: product.isActive ? 'Producto desactivado' : 'Producto activado' });
            setConfirmActive(false);
        } catch (error: unknown) {
            notify({ type: 'err', text: error instanceof Error ? error.message : String(error) });
        }
    };

    const onSaveVariant = async (variant: StoreMerchVariant) => {
        if (!draft) return;
        const current = draft.variants.find((item) => item.id === variant.id);
        if (!current) return;
        const priceCents = parseEurToCents(current.priceEur);
        if (priceCents == null) {
            notify({ type: 'err', text: `Precio inválido en ${variant.sku}` });
            return;
        }
        try {
            await updateVariant.mutateAsync({
                variantId: variant.id,
                priceCents,
                isActive: current.isActive,
                stripePriceId: current.stripePriceId.trim() || null,
            });
            setBaseline(merchBaselineAfterVariantSave(baseline, current));
            notify({ type: 'ok', text: `Variante ${variant.sku} guardada` });
        } catch (error: unknown) {
            notify({ type: 'err', text: error instanceof Error ? error.message : String(error) });
        }
    };

    if (!merchId) {
        return <p className="p-6 text-sm text-destructive">Identificador de producto no válido.</p>;
    }
    if (catalog.isLoading || (product && !draft)) {
        return (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Cargando producto…
            </div>
        );
    }
    if (catalog.isError || !product || !draft) {
        return (
            <p className="p-6 text-sm text-destructive">
                {(catalog.error as Error)?.message || 'No se encontró el producto de merch'}
            </p>
        );
    }

    const categoryOptions = CATEGORIES.some((item) => item.value === draft.category)
        ? [...CATEGORIES]
        : [...CATEGORIES, { value: draft.category, label: draft.category }];
    const categoryLabel = categoryOptions.find((item) => item.value === draft.category)?.label ?? draft.category;
    const image = catalogImageUrl(null, product.images?.[0]);

    return (
        <div data-page="store-merch-detail" data-product-id={product.id}>
            <ResourceDetail
                crumbs={[
                    { label: 'Productos', to: productsHref },
                    { label: draft.name || 'Merch' },
                ]}
                title={draft.name || 'Merch'}
                unsavedMessage={dirty ? 'Cambios sin guardar' : null}
                onDiscard={() => {
                    if (!baseline) return;
                    setDraft(JSON.parse(baseline) as MerchDraft);
                }}
                badge={<StatusBadge status={product.isActive ? 'active' : 'inactive'} />}
                actions={(
                    <>
                        <Button type="button" size="sm" onClick={() => void onSave()} disabled={updateProduct.isPending}>
                            {updateProduct.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                            Guardar
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => setConfirmActive(true)}>
                            {product.isActive ? 'Desactivar' : 'Activar'}
                        </Button>
                    </>
                )}
                aside={(
                    <div className="space-y-3">
                        <section className="space-y-2 rounded-lg border border-border p-3">
                            <h2 className="text-sm font-semibold">Publicación</h2>
                            <p className="text-sm">{product.isActive ? 'Activo en la tienda' : 'No se vende'}</p>
                        </section>
                        <section className="space-y-2 rounded-lg border border-border p-3">
                            <h2 className="text-sm font-semibold">Organización</h2>
                            <div className="space-y-1">
                                <Label>Categoría</Label>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button type="button" variant="outline" size="sm" className="w-full justify-between">
                                            {categoryLabel}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        {categoryOptions.map((item) => (
                                            <DropdownMenuItem
                                                key={item.value}
                                                onSelect={() => setDraft((prev) => (prev ? { ...prev, category: item.value } : prev))}
                                            >
                                                {item.label}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="merch-order">Orden</Label>
                                <Input
                                    id="merch-order"
                                    inputMode="numeric"
                                    value={draft.sortOrder}
                                    onChange={(event) => setDraft((prev) => (
                                        prev ? { ...prev, sortOrder: event.target.value } : prev
                                    ))}
                                />
                            </div>
                        </section>
                        <details className="rounded-lg border border-border p-3">
                            <summary className="cursor-pointer text-sm font-semibold">Avanzado</summary>
                            <div className="mt-3 space-y-3">
                                <p className="font-mono text-xs text-muted-foreground">{product.slug}</p>
                                {product.variants.map((variant) => {
                                    const current = draft.variants.find((item) => item.id === variant.id);
                                    if (!current) return null;
                                    return (
                                        <div key={variant.id} className="space-y-1">
                                            <Label htmlFor={`stripe-${variant.id}`}>
                                                Stripe price id · {variant.sku}
                                            </Label>
                                            <Input
                                                id={`stripe-${variant.id}`}
                                                value={current.stripePriceId}
                                                onChange={(event) => setVariant(variant.id, { stripePriceId: event.target.value })}
                                                placeholder="price_…"
                                            />
                                        </div>
                                    );
                                })}
                                <p className="text-[11px] text-muted-foreground">
                                    El identificador de Stripe se guarda con el botón de cada variante.
                                </p>
                            </div>
                        </details>
                    </div>
                )}
            >
                <div className="space-y-4">
                    <div className="space-y-1">
                        <Label htmlFor="merch-name">Nombre</Label>
                        <Input
                            id="merch-name"
                            value={draft.name}
                            onChange={(event) => setDraft((prev) => (prev ? { ...prev, name: event.target.value } : prev))}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="merch-description">Descripción</Label>
                        <Textarea
                            id="merch-description"
                            rows={4}
                            value={draft.description}
                            onChange={(event) => setDraft((prev) => (
                                prev ? { ...prev, description: event.target.value } : prev
                            ))}
                        />
                    </div>
                    {image ? (
                        <img src={image} alt={draft.name} className="max-h-56 rounded-lg border object-contain" />
                    ) : (
                        <p className="text-sm text-muted-foreground">Sin imagen.</p>
                    )}
                    <section className="space-y-3">
                        <h2 className="text-sm font-semibold">Variantes</h2>
                        <p className="text-[11px] text-muted-foreground">
                            El precio se guarda aquí. El stock está en el bloque de abajo.
                        </p>
                        {product.variants.map((variant) => {
                            const current = draft.variants.find((item) => item.id === variant.id);
                            if (!current) return null;
                            return (
                                <div key={variant.id} className="space-y-2 rounded-lg border p-3">
                                    <div className="flex justify-between gap-2 text-xs text-muted-foreground">
                                        <span className="font-mono">{variant.sku}</span>
                                        <span>
                                            {variant.size || 'única'}
                                            {variant.color ? ` · ${variant.color}` : ''}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Ahora: {formatEurFromCents(variant.priceCents)}
                                    </p>
                                    <div className="space-y-1">
                                        <Label htmlFor={`price-${variant.id}`}>Precio (€)</Label>
                                        <Input
                                            id={`price-${variant.id}`}
                                            inputMode="decimal"
                                            value={current.priceEur}
                                            onChange={(event) => setVariant(variant.id, { priceEur: event.target.value })}
                                        />
                                    </div>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={current.isActive}
                                            onChange={(event) => setVariant(variant.id, { isActive: event.target.checked })}
                                        />
                                        Variante activa
                                    </label>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => void onSaveVariant(variant)}
                                        disabled={updateVariant.isPending}
                                    >
                                        Guardar variante
                                    </Button>
                                </div>
                            );
                        })}
                    </section>
                    <section className="space-y-3">
                        <h2 className="text-sm font-semibold">Stock</h2>
                        <MerchStockBoard productId={product.id} />
                    </section>
                </div>
            </ResourceDetail>
            <AdminConfirmDialog
                open={leave.pendingHref != null}
                title="Hay cambios sin guardar"
                description="Si sales ahora, se pierden los cambios de este producto."
                confirmLabel="Salir sin guardar"
                variant="danger"
                onConfirm={leave.confirmLeave}
                onCancel={leave.cancelLeave}
            />
            <AdminConfirmDialog
                open={confirmActive}
                title={product.isActive ? '¿Desactivar este producto?' : '¿Activar este producto?'}
                description={product.isActive
                    ? 'Dejará de verse a la venta. Las variantes se conservan.'
                    : 'Volverá a poder venderse.'}
                confirmLabel={product.isActive ? 'Desactivar' : 'Activar'}
                variant={product.isActive ? 'danger' : 'default'}
                confirmDisabled={updateProduct.isPending}
                onConfirm={() => void onToggleActive()}
                onCancel={() => setConfirmActive(false)}
            />
        </div>
    );
}
